// @ts-nocheck
// Finder/System-Settings style navigator. Frosted layer, status roll-up,
// full keyboard control (↑↓ move, →← expand/collapse, Enter opens).
import { useEffect, useMemo, useRef } from 'react';
import { AlertTriangle, Check, ChevronRight, Circle, Pause } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { rollUp, visibleRows } from './treeState';

const STATUS_ICON = {
  done: { Icon: Check, className: 'text-[hsl(var(--success))]', label: 'done' },
  attention: { Icon: AlertTriangle, className: 'text-[hsl(var(--warning))]', label: 'needs attention' },
  paused: { Icon: Pause, className: 'text-muted-foreground', label: 'paused' },
  empty: { Icon: Circle, className: 'text-muted-foreground/60', label: 'empty' },
};

// red "!" only on a required row that is not done yet (never on parent groups)
const needsBang = (row, statuses) => row.need === 'required' && statuses[row.id] !== 'done';

function StatusDot({ status }) {
  const { Icon, className, label } = STATUS_ICON[status] || STATUS_ICON.empty;
  return (
    <span title={label} aria-label={label} className="shrink-0">
      <Icon className={cn('h-3.5 w-3.5', className)} strokeWidth={2.5} />
    </span>
  );
}

export default function BuilderTree({ nodes, selectedId, onSelect, expanded, onToggle, className }) {
  const statuses = useMemo(() => rollUp(nodes), [nodes]);
  const rows = useMemo(() => visibleRows(nodes, expanded), [nodes, expanded]);
  const listRef = useRef(null);

  // keep the selected row in view when the palette or a checklist click jumps
  useEffect(() => {
    listRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const onKeyDown = (e) => {
    const i = rows.findIndex((r) => r.id === selectedId);
    const row = rows[i];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      onSelect(rows[Math.min(i + 1, rows.length - 1)]?.id ?? rows[0]?.id);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onSelect(rows[Math.max(i - 1, 0)]?.id ?? rows[0]?.id);
    } else if (e.key === 'ArrowRight' && row?.children?.length && !expanded[row.id]) {
      e.preventDefault();
      onToggle(row.id);
    } else if (e.key === 'ArrowLeft' && row?.children?.length && expanded[row.id]) {
      e.preventDefault();
      onToggle(row.id);
    }
  };

  return (
    <nav
      ref={listRef}
      aria-label="Agent sections"
      role="tree"
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={cn(
        'overflow-y-auto rounded-2xl border border-border p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        // frosted layer, with a solid fallback where backdrop-filter is off
        'bg-card/80 supports-[backdrop-filter]:bg-card/55 supports-[backdrop-filter]:backdrop-blur-xl',
        'motion-reduce:supports-[backdrop-filter]:bg-card',
        className,
      )}
    >
      {rows.map((row) => {
        const isParent = !!row.children?.length;
        const selected = row.id === selectedId;
        return (
          <div
            key={row.id}
            role="treeitem"
            aria-selected={selected}
            aria-expanded={isParent ? !!expanded[row.id] : undefined}
            data-selected={selected}
            onClick={() => {
              onSelect(row.id);
              if (isParent) onToggle(row.id);
            }}
            style={{ paddingLeft: 8 + row.depth * 14 }}
            className={cn(
              'group flex cursor-pointer items-center gap-2 rounded-lg py-1.5 pr-2 text-sm transition-colors',
              selected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/70',
              row.depth > 0 && !selected && 'text-muted-foreground',
            )}
          >
            {isParent ? (
              <ChevronRight
                className={cn(
                  'h-3.5 w-3.5 shrink-0 transition-transform duration-200 motion-reduce:transition-none',
                  expanded[row.id] && 'rotate-90',
                )}
              />
            ) : (
              <span className="w-3.5 shrink-0" />
            )}
            {row.need === 'required' && (
              <span aria-hidden className={cn('-mr-1 shrink-0 font-bold leading-none', selected ? 'text-primary-foreground' : 'text-destructive')}>*</span>
            )}
            <span className="truncate">{row.label}</span>
            {!selected && needsBang(row, statuses) && (
              <span title="Required" aria-label="Required, not done yet" className="shrink-0 text-sm font-bold leading-none text-destructive">
                !
              </span>
            )}
            <span className="ml-auto flex items-center gap-1.5">
              {row.need === 'optional' && !isParent && (
                <span className={cn('text-[10px] italic', selected ? 'opacity-80' : 'text-muted-foreground/80')}>optional</span>
              )}
              {row.badge && (
                <span className={cn('text-[10px]', selected ? 'opacity-80' : 'text-muted-foreground')}>
                  {row.badge}
                </span>
              )}
              <StatusDot status={statuses[row.id]} />
            </span>
          </div>
        );
      })}
    </nav>
  );
}
